import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  threeDotsIcon,
  ViewDetailsIcon,
  CopyIcon,
  BlackAddIcon,
  EditProgramIcon,
  ManageCodesIcon,
  PauseProgramIcon,
  DeleteProgramIcon,
} from "../../assets/icons";
import Table from "../../components/Tables";
import PaginationTab from "../../components/PaginationTab";
import { useDispatch, useSelector } from "react-redux";
import {
  reqToExportprogram,
  reqToGetApplicationList,
} from "../../redux/slices.js/discountmanagement/discountPromotionSlice";

const ApplicationTab = ({ openRow, setOpenRow, dropdownRef }) => {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIds, setSelectedIds] = useState([]); // ✅ Track selected rows
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    tenant: "",
    program: "",
    code: "",
    createdAt: "",
  });
  const totalCount = useSelector(
    (state) => state?.discountCoupons?.applicationTotalCount || 0
  );

  const totalPages = Math.ceil(totalCount / pageSize);

  const filterDropdownRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    if (isFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterOpen]);

  const toggleFilterDropdown = (e) => {
    e.stopPropagation();
    setIsFilterOpen(prev => !prev);
  };

  const resetFilters = () => {
    setFilters({ tenant: "", program: "", code: "", createdAt: "" });
  };

  // When final "Apply Filter" clicked (main drawer button)
  const handleApplyAllFilters = () => {
    setIsFilterOpen(false);

    dispatch(
      reqToGetApplicationList({
        data: {
          page: 1,
          limit: pageSize,
          ...filters,
        },
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  };

  const applicationList = useSelector(
    (state) => state?.discountCoupons?.applicationList
  );

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Trigger API with filters
  const applyFilters = () => {
    dispatch(
      reqToGetApplicationList({
        data: {
          page: 1,
          limit: pageSize,
          ...filters,
        },
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  };

  useEffect(() => {
    dispatch(
      reqToGetApplicationList({
        data: { page: currentPage, limit: pageSize },
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  }, [dispatch, currentPage, pageSize]);

  // ✅ CORRECTED: Transform data based on your real API structure
  const transformedData = useMemo(() => {
    if (!applicationList?.length) return [];

    return applicationList.map((item) => ({
      id: item.id,
      tenant: item.tenant?.name || `Tenant #${item.tenant_id}`,
      tenantId: item.tenant_id,
      tenantStatus: item.tenant?.status || "N/A",
      program: item.source || "N/A", // "RULE" or "COUPON"
      discount: `$${item.applied_amount || 0} USD`,
      progress: {
        current: item.remaining_cycles ? 999 - item.remaining_cycles : 0,
        total: 999,
      },
      progressPercent: item.remaining_cycles
        ? Math.round(((999 - item.remaining_cycles) / 999) * 100)
        : 0,
      mrr_impact: item.mrr || 0,
      createdAt: item.applied_at || item.createdAt,
      status: item.status,
      user: { name: `Discount ${item?.discountcode?.name}` },
      // Additional data for export
      discountProgram: item.discountProgram,
      discountcode: item.discountcode,
      remaining_cycles: item.remaining_cycles,
      source: item.source,
      applied_amount: item.applied_amount,
    }));
  }, [applicationList]);

  // ✅ Handle checkbox logic
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(transformedData.map((row) => row.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const isAllSelected =
    transformedData.length > 0 && selectedIds.length === transformedData.length;

  // ✅ Handle export click
  const handleExport = () => {
    const payload = {
      id: selectedIds,
    };

    const exportToCSV = (records) => {
      if (!Array.isArray(records) || records.length === 0) return;

      // Define headers for relevant data only
      const headers = [
        "Application ID",
        "Tenant Name",
        "Tenant ID",
        "Program Name",
        "Discount Type",
        "Code Name",
        "Applied Amount",
        "MRR Impact",
        "Status",
        "Remaining Cycles",
        "Applied Date",
        "Discount Value",
        "Source",
      ];

      const escapeCSV = (val) => {
        if (val === null || val === undefined) return "";
        const str = String(val);
        if (/[",\n]/.test(str)) {
          return '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
      };

      // Map records to include only relevant data
      const rows = records.map((record) => [
        record.id,
        record.tenant?.name || "N/A",
        record.tenant_id,
        record.discountProgram?.name || "N/A",
        record.discountProgram?.discount_type === 1 ? "Percentage" : "Fixed",
        record.discountcode?.name || "N/A",
        `$${record.applied_amount || 0}`,
        `$${record.mrr || 0}`,
        record.status,
        record.remaining_cycles || 0,
        new Date(record.applied_at).toLocaleDateString(),
        `${record.discountProgram?.discount_value || 0}%`,
        record.source,
      ]);

      const csv = [
        headers.join(","),
        ...rows.map((row) => row.map((cell) => escapeCSV(cell)).join(",")),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const date = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `applications_export_${date}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    dispatch(
      reqToExportprogram({
        data: payload,
        onSuccess: (response) => {
          const records = response?.result || response?.data?.result || [];
          exportToCSV(records);
        },
        onFailure: () => {
          console.error("Export failed");
        },
      })
    );
  };

  // ✅ CORRECTED: Columns with proper data mapping
  const columns = [
    {
      key: "select",
      dataIndex: "select",
      title: (
        <input
          type="checkbox"
          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
          checked={isAllSelected}
          onChange={handleSelectAll}
        />
      ),
      render: (_, record) => (
        <input
          type="checkbox"
          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
          checked={selectedIds.includes(record.id)}
          onChange={() => handleSelectRow(record.id)}
        />
      ),
    },
    {
      key: "tenant",
      dataIndex: "tenant",
      title: "Tenant",
      render: (value, record) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{record.tenantStatus}</div>
        </div>
      ),
    },
    {
      key: "program",
      dataIndex: "program",
      title: "Program",
      render: (value) => <div className="text-gray-900">{value}</div>,
    },
    {
      key: "discount",
      dataIndex: "discount",
      title: "Discount",
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          {value}
        </span>
      ),
    },
    {
      key: "progress",
      dataIndex: "progress",
      title: "Progress",
      render: (value, record) => (
        <div className="flex flex-col gap-1">
          <div className="text-gray-900">
            {record.progress.current}/{record.progress.total}
          </div>
          <div className="flex items-center gap-2">
            <div className="w-20 h-2 bg-gray-200 rounded-full">
              <div
                className="h-2 bg-orange-400 rounded-full"
                style={{ width: `${record.progressPercent}%` }}
              ></div>
            </div>
            <span className="text-xs text-gray-500">
              {record.progressPercent}%
            </span>
          </div>
        </div>
      ),
    },
    {
      key: "mrrImpact",
      dataIndex: "mrr_impact",
      title: "MRR Impact",
      render: (value) => (
        <div className="text-sm font-medium text-orange-600">${value}</div>
      ),
    },
    {
      key: "applied",
      dataIndex: "createdAt",
      title: "Applied",
      render: (value, record) => (
        <div>
          <div className="text-gray-900">
            {new Date(value).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </div>
          <div className="text-gray-500">{record.user?.name || "N/A"}</div>
        </div>
      ),
    },
    {
      key: "status",
      dataIndex: "status",
      title: "Status",
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          {value?.charAt(0)?.toUpperCase() + value?.slice(1)}
        </span>
      ),
    },
  ];

  const rowActions = {
    threeDotsIcon: threeDotsIcon,
    actions: (row) => [
      {
        label: "View details",
        icon: ViewDetailsIcon,
        to: "#",
        onClick: (row) => console.log("View details clicked for:", row),
      },
      {
        label: "Edit program",
        icon: EditProgramIcon,
        to: "#",
        onClick: (row) => console.log("Edit program clicked for:", row),
      },
      {
        label: "Manage codes",
        icon: ManageCodesIcon,
        to: "#",
        onClick: (row) => console.log("Manage codes clicked for:", row),
      },
      {
        label: row.status === "active" ? "Pause Program" : "Activate Program",
        icon: PauseProgramIcon,
        to: "#",
        onClick: (row) =>
          console.log("Pause/Activate Program clicked for:", row),
      },
      {
        label: "Delete Program",
        icon: DeleteProgramIcon,
        to: "#",
        onClick: (row) => console.log("Delete Program clicked for:", row),
      },
    ],
  };

  return (
    <>
      <div className="">
        <div className="flex gap-4">
          <div className="relative" ref={filterDropdownRef}>
            <button
              className="btn-light-gray flex items-center gap-1"
              onClick={toggleFilterDropdown}
            >
              {BlackAddIcon} Filter
            </button>
            {isFilterOpen && (
              <div className="absolute left-0 mt-2 z-50 w-64 bg-white border rounded-lg shadow-lg p-3">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Tenant
                    </label>
                    <input
                      type="text"
                      name="tenant"
                      value={filters.tenant}
                      onChange={handleFilterChange}
                      className="border border-gray-300 rounded-md px-2 py-1 w-full"
                      placeholder="Enter tenant"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Program
                    </label>
                    <input
                      type="text"
                      name="program"
                      value={filters.program}
                      onChange={handleFilterChange}
                      className="border border-gray-300 rounded-md px-2 py-1 w-full"
                      placeholder="Enter program"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Code
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={filters.code}
                      onChange={handleFilterChange}
                      className="border border-gray-300 rounded-md px-2 py-1 w-full"
                      placeholder="Enter code"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Created At
                    </label>
                    <input
                      type="date"
                      name="createdAt"
                      value={filters.createdAt}
                      onChange={handleFilterChange}
                      className="border border-gray-300 rounded-md px-2 py-1 w-full"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={resetFilters}
                    className="w-1/2 border border-gray-300 rounded-lg py-2 text-gray-700"
                  >
                    Reset
                  </button>
                  <button
                    onClick={handleApplyAllFilters}
                    className="w-1/2 bg-blue-600 text-white rounded-lg py-2"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            className={`btn-light-gray ${
              selectedIds.length === 0 ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={selectedIds.length === 0}
            onClick={handleExport}
          >
            {BlackAddIcon} Export
          </button>

          <button className="btn-light-gray">{BlackAddIcon} Bulk revoke</button>
        </div>

        <Table
          columns={columns}
          data={transformedData}
          keyField="id"
          // rowActions={rowActions}
          openRow={openRow}
          setOpenRow={setOpenRow}
          dropdownRef={dropdownRef}
          emptyMessage="No applications found"
          onRowClick={(row) => console.log("Row clicked:", row)}
        />

        <PaginationTab
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      </div>
      {/* Dropdown UI replaces previous side drawer */}
    </>
  );
};

export default ApplicationTab;
